<template>
  <v-dialog :value="dialog" persistent max-width="520" @keydown.esc="close">
    <v-card class="teacher-password-dialog">
      <v-card-title>
        <div>
          <div class="teacher-password-dialog__eyebrow">Teacher portal</div>
          <div class="teacher-password-dialog__title">Reset password</div>
          <div class="teacher-password-dialog__subtitle">
            Set a new password for {{ teacher ? teacher.name : "this teacher" }}. You can then give the password directly to the teacher.
          </div>
        </div>
        <v-btn icon aria-label="Close password reset dialog" :disabled="saving" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert type="info" text dense>
          Login email: {{ loginEmail }}
        </v-alert>
        <v-text-field
          v-model="newPassword"
          :type="showPassword ? 'text' : 'password'"
          :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
          :error-messages="passwordErrors"
          :hide-details="passwordErrors.length === 0"
          label="New password"
          outlined
          autocomplete="new-password"
          @click:append="showPassword = !showPassword"
          @keyup.enter="submit"
        />
        <v-text-field
          v-model="confirmPassword"
          :type="showPassword ? 'text' : 'password'"
          :error-messages="confirmationErrors"
          :hide-details="confirmationErrors.length === 0"
          label="Confirm new password"
          outlined
          autocomplete="new-password"
          @keyup.enter="submit"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="saving" @click="close">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="saving" @click="submit">
          Reset password
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import teacherService from "../service";

export default {
  name: "teacher-password-reset-dialog",

  props: {
    dialog: Boolean,
    teacher: Object,
  },

  data() {
    return {
      newPassword: "",
      confirmPassword: "",
      showPassword: false,
      submitted: false,
      saving: false,
    };
  },

  computed: {
    loginEmail() {
      return this.teacher && this.teacher.LoginUser ? this.teacher.LoginUser.email : "Not set";
    },

    passwordErrors() {
      if (!this.submitted) return [];
      if (!this.newPassword) return ["Enter a new password"];
      if (this.newPassword.length < 8) return ["Password must be at least 8 characters"];
      return [];
    },

    confirmationErrors() {
      if (!this.submitted) return [];
      if (!this.confirmPassword) return ["Confirm the new password"];
      if (this.confirmPassword !== this.newPassword) return ["Passwords do not match"];
      return [];
    },
  },

  watch: {
    dialog(value) {
      if (value) this.resetForm();
    },
  },

  methods: {
    resetForm() {
      this.newPassword = "";
      this.confirmPassword = "";
      this.showPassword = false;
      this.submitted = false;
      this.saving = false;
    },

    close() {
      if (!this.saving) this.$emit("close");
    },

    async submit() {
      this.submitted = true;
      if (!this.teacher || this.passwordErrors.length || this.confirmationErrors.length || this.saving) return;
      this.saving = true;
      try {
        const teacherName = this.teacher.name;
        await teacherService.resetPassword(this.teacher.id, { newPassword: this.newPassword });
        this.$emit("close");
        this.$swal({
          title: "Password updated",
          text: `The portal password for ${teacherName} has been reset.`,
          icon: "success",
        });
      } catch (error) {
        const message = error && error.data && error.data.message
          ? error.data.message
          : "Unable to reset this teacher's password.";
        this.$swal({ title: "Password was not updated", text: message, icon: "error" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.teacher-password-dialog {
  border-radius: 12px;
}

.teacher-password-dialog .v-card__title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 24px 14px;
}

.teacher-password-dialog__eyebrow {
  margin-bottom: 4px;
  color: #43a047;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.teacher-password-dialog__title {
  color: #172033;
  font-size: 22px;
  font-weight: 750;
}

.teacher-password-dialog__subtitle {
  max-width: 400px;
  margin-top: 5px;
  color: #728095;
  font-size: 12px;
  line-height: 1.5;
}

.teacher-password-dialog .v-card__text {
  padding: 10px 24px 6px;
}

.teacher-password-dialog .v-card__actions {
  padding: 12px 24px 22px;
}

.teacher-password-dialog .v-card__actions .v-btn {
  border-radius: 8px;
  letter-spacing: 0;
  text-transform: none;
}

@media (max-width: 599px) {
  .teacher-password-dialog .v-card__title,
  .teacher-password-dialog .v-card__text,
  .teacher-password-dialog .v-card__actions {
    padding-right: 18px;
    padding-left: 18px;
  }
}
</style>
